import { useEffect, useState } from "react";
import { ArrowLeft, FileText, Globe } from "lucide-react";
import { Button } from "@/components/ui/button";
import LicenseRequestPanel from "@/components/LicenseRequestPanel";
import LicenseRequestHistory from "@/components/LicenseRequestHistory";
import LicensePdfManualPanel from "@/components/LicensePdfManualPanel";
import LicenseSubmissionGate from "@/components/LicenseSubmissionGate";
import LicenseCommercialSharedForm from "@/components/LicenseCommercialSharedForm";
import LicenseCommercialScopeForm from "@/components/LicenseCommercialScopeForm";
import LicenseCommercialSummary from "@/components/LicenseCommercialSummary";
import LicenseCommercialLegalSection from "@/components/LicenseCommercialLegalSection";
import LicenseCommercialActivationCollapsible from "@/components/LicenseCommercialActivationCollapsible";
import LicenseExpansionForm from "@/components/LicenseExpansionForm";
import {
  LICENSE_CHANNEL_LABELS,
  LICENSE_CHANNEL_PDF,
  LICENSE_CHANNEL_WEB,
} from "@/lib/licenseCommercialFlow";
import {
  buildLicenseRequestFormDefaults,
  canManageCommercialLicenseRequests,
  hasActiveCommercialLicense,
  isSubmissionBlocked,
  LICENSE_REQUEST_MSG_ACTIVE_LICENSE,
  LICENSE_REQUEST_MSG_LEGAL_NOT_ACCEPTED,
} from "@/lib/commercialLicenseRequests";
import {
  DEFAULT_INVOICE_CFDI_USE,
  DEFAULT_INVOICE_PAYMENT_METHOD,
} from "@/lib/commercialInvoiceCatalog";
import { useCommercialSubmissionGate } from "@/lib/useCommercialSubmissionGate";
import { useCommercialLegalAcceptance } from "@/lib/useCommercialLegalAcceptance";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";

const CHANNEL_CARDS = [
  {
    id: LICENSE_CHANNEL_WEB,
    title: "Solicitud web de licencia",
    description:
      "Envía tu solicitud en línea con plan, cadencia y tipo de licencia. BNT la revisará en el Centro SaaS.",
    icon: Globe,
    testId: "license-commercial-card-web",
  },
  {
    id: LICENSE_CHANNEL_PDF,
    title: "Solicitud por PDF / manual",
    description:
      "Genera texto o PDF con tu plan seleccionado para enviar por correo o WhatsApp a BNT.",
    icon: FileText,
    testId: "license-commercial-card-pdf",
  },
];

/**
 * Hub comercial comprador: plan + factura → dos tarjetas → formulario del canal elegido.
 */
export default function LicenseCommercialSection({
  systemInfo,
  profileDefaults = null,
  onLicenseInstalled,
  focusMode = false,
  buyerCleanLayout = false,
}) {
  const { user } = useAuth();
  const profile = profileDefaults || systemInfo?.profile || {};
  const canManage = canManageCommercialLicenseRequests(user);
  const [channel, setChannel] = useState(null);
  const [renewalOpen, setRenewalOpen] = useState(false);
  const [form, setForm] = useState(() => buildLicenseRequestFormDefaults(systemInfo, profile));
  const { gate, setGate, refreshGate } = useCommercialSubmissionGate(user, canManage);
  const sharedLegal = useCommercialLegalAcceptance(canManage, { form, user, systemInfo });

  useEffect(() => {
    setForm(buildLicenseRequestFormDefaults(systemInfo, profile));
  }, [systemInfo, profileDefaults]);

  const handleGateChange = (nextGate) => {
    setGate(nextGate);
    refreshGate();
  };

  const applyRegistrationInvoiceData = (checked) => {
    setForm((prev) => ({
      ...prev,
      use_registration_invoice_data: checked,
      invoice_rfc: checked ? (profile.rfc || prev.rfc || "").trim().toUpperCase() : prev.invoice_rfc,
      invoice_razon_social: checked
        ? (profile.razon_social || prev.razon_social || "").trim()
        : prev.invoice_razon_social,
      invoice_email: checked
        ? (profile.correo_facturacion || profile.email || prev.email || "").trim()
        : prev.invoice_email,
      invoice_codigo_postal: checked
        ? (profile.codigo_postal || prev.invoice_codigo_postal || "").trim()
        : prev.invoice_codigo_postal,
      invoice_fiscal_address: checked
        ? (profile.domicilio || prev.invoice_fiscal_address || "").trim()
        : prev.invoice_fiscal_address,
      invoice_regimen_fiscal: checked
        ? (profile.regimen_fiscal || prev.invoice_regimen_fiscal || "").trim()
        : prev.invoice_regimen_fiscal,
      invoice_cfdi_use: checked ? DEFAULT_INVOICE_CFDI_USE : prev.invoice_cfdi_use,
      invoice_payment_method: checked ? DEFAULT_INVOICE_PAYMENT_METHOD : prev.invoice_payment_method,
    }));
  };

  const submissionBlocked = isSubmissionBlocked(gate);
  const hasActiveLicense = hasActiveCommercialLicense(systemInfo);
  const showInitialRequestFlow = !hasActiveLicense || renewalOpen;
  const hideTrialModulesInForm = buyerCleanLayout && hasActiveLicense;

  const handleChannelSelect = (channelId) => {
    if (!sharedLegal.canProceedWithRequest) {
      toast.error(LICENSE_REQUEST_MSG_LEGAL_NOT_ACCEPTED);
      document
        .querySelector('[data-testid="license-commercial-legal-section"]')
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    setChannel(channelId);
  };

  const sharedOmitSections = {
    profile: true,
    modules: true,
    invoice: true,
    licenseType: true,
    scope: true,
    paymentProof: true,
    legal: true,
  };

  return (
    <div className="space-y-4" data-testid="license-commercial-section">
      {focusMode ? (
        <LicenseCommercialSummary systemInfo={systemInfo} profile={profile} showEditTitular />
      ) : null}

      {hasActiveLicense && buyerCleanLayout && !renewalOpen ? (
        <div className="space-y-3" data-testid="license-commercial-renewal-card">
          <h4 className="text-sm font-semibold text-slate-900 m-0">Renovación o ampliación</h4>
          <p
            className="text-xs text-sky-900 bg-sky-50 border border-sky-200 rounded-md px-3 py-2 m-0"
            data-testid="license-commercial-active-license-notice"
          >
            {LICENSE_REQUEST_MSG_ACTIVE_LICENSE}
          </p>
          <Button
            type="button"
            className="bg-[#0F2D5C] hover:bg-[#0a2247] h-9"
            onClick={() => setRenewalOpen(true)}
            data-testid="license-commercial-renewal-open"
          >
            Solicitar renovación o ampliación
          </Button>
        </div>
      ) : null}

      {hasActiveLicense && !buyerCleanLayout ? (
        <p
          className="text-[10px] text-sky-900 bg-sky-50 border border-sky-200 rounded-sm px-2.5 py-2 m-0"
          data-testid="license-commercial-active-license-notice"
        >
          {LICENSE_REQUEST_MSG_ACTIVE_LICENSE}
        </p>
      ) : null}

      {showInitialRequestFlow ? (
        <>
          {hasActiveLicense && buyerCleanLayout && renewalOpen ? (
            <>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <h4 className="text-sm font-semibold text-slate-900 m-0">Solicitud de renovación o ampliación</h4>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="h-8 text-xs"
                  onClick={() => setRenewalOpen(false)}
                >
                  Cancelar
                </Button>
              </div>
              <LicenseExpansionForm
                systemInfo={systemInfo}
                profileDefaults={profileDefaults}
                gate={gate}
                onGateChange={handleGateChange}
                onSubmitted={refreshGate}
                user={user}
              />
            </>
          ) : (
            <>
              {!buyerCleanLayout ? (
                <h4 className="text-sm font-semibold text-slate-900 m-0">Solicitud de licencia</h4>
              ) : (
                <h4 className="text-sm font-semibold text-slate-900 m-0">Solicitar licencia</h4>
              )}

              <LicenseCommercialSharedForm
            form={form}
            setForm={setForm}
            systemInfo={systemInfo}
            profile={profile}
            onApplyRegistrationInvoiceData={applyRegistrationInvoiceData}
            hideTrialModules={hideTrialModulesInForm}
          />

          <LicenseCommercialScopeForm
            form={form}
            setForm={setForm}
            showComments
            disabled={submissionBlocked}
          />

          {canManage ? (
            <LicenseCommercialLegalSection
              legalCatalog={sharedLegal.legalCatalog}
              legalLoading={sharedLegal.legalLoading}
              legalError={sharedLegal.legalError}
              legalAccepted={sharedLegal.legalAccepted}
              legalLocked={sharedLegal.legalLocked}
              legalSaving={sharedLegal.legalSaving}
              legalAcceptError={sharedLegal.legalAcceptError}
              agreementAcceptanceId={sharedLegal.agreementAcceptanceId}
              onLegalAcceptedChange={sharedLegal.handleLegalAcceptedChange}
              disabled={submissionBlocked}
            />
          ) : null}

          {canManage ? (
            <LicenseSubmissionGate gate={gate} onGateChange={handleGateChange} />
          ) : null}

          <div className="space-y-1 pt-1 border-t border-slate-100">
            <h4 className="text-sm font-semibold text-slate-900 m-0">Método de solicitud</h4>
            <p className="text-[11px] text-slate-600 m-0 leading-snug">
              Elija un solo método. BNT revisará y activará los cambios desde el Centro SaaS.
            </p>
          </div>

          {!sharedLegal.canProceedWithRequest && canManage ? (
            <p
              className="text-[10px] text-amber-900 bg-amber-50 border border-amber-200 rounded-sm px-2.5 py-2 m-0"
              data-testid="license-commercial-channel-legal-gate"
            >
              {LICENSE_REQUEST_MSG_LEGAL_NOT_ACCEPTED}
            </p>
          ) : null}

          {!channel ? (
            <div className="grid gap-3 sm:grid-cols-2" data-testid="license-commercial-channel-cards">
              {CHANNEL_CARDS.map(({ id, title, description, icon: Icon, testId }) => (
                <button
                  key={id}
                  type="button"
                  className={`rounded-lg border bg-white p-4 text-left shadow-sm transition-colors ${
                    sharedLegal.canProceedWithRequest
                      ? "border-slate-200 hover:border-[#0F2D5C]/40 hover:bg-slate-50/80 cursor-pointer"
                      : "border-amber-200 bg-amber-50/40 cursor-pointer opacity-90"
                  }`}
                  onClick={() => handleChannelSelect(id)}
                  aria-disabled={!sharedLegal.canProceedWithRequest}
                  data-testid={testId}
                >
                  <div className="flex items-start gap-3">
                    <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-[#0F2D5C]/10 text-[#0F2D5C]">
                      <Icon className="h-4 w-4" />
                    </span>
                    <span className="space-y-1">
                      <span className="block text-sm font-semibold text-[#0F2D5C]">{title}</span>
                      <span className="block text-[11px] text-slate-600 leading-snug">{description}</span>
                    </span>
                  </div>
                </button>
              ))}
            </div>
          ) : (
            <div className="space-y-3">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-8 px-2 text-xs text-slate-700"
                onClick={() => setChannel(null)}
                data-testid="license-commercial-back"
              >
                <ArrowLeft className="w-3.5 h-3.5 mr-1" />
                Cambiar método ({LICENSE_CHANNEL_LABELS[channel]})
              </Button>

              {channel === LICENSE_CHANNEL_WEB ? (
                <LicenseRequestPanel
                  systemInfo={systemInfo}
                  profileDefaults={profileDefaults}
                  showSectionHeader={false}
                  showHistory={false}
                  showActiveModulesNotice={false}
                  submissionGate={gate}
                  onSubmissionGateChange={handleGateChange}
                  onRequestSubmitted={refreshGate}
                  form={form}
                  setForm={setForm}
                  omitSections={sharedOmitSections}
                  sharedLegal={sharedLegal}
                />
              ) : null}

              {channel === LICENSE_CHANNEL_PDF ? (
                <LicensePdfManualPanel
                  systemInfo={systemInfo}
                  profileDefaults={profileDefaults}
                  onLicenseInstalled={onLicenseInstalled}
                  submissionGate={gate}
                  onSubmissionGateChange={handleGateChange}
                  onRequestSubmitted={refreshGate}
                  form={form}
                  setForm={setForm}
                  omitSections={sharedOmitSections}
                  showActivationSection={false}
                  sharedLegal={sharedLegal}
                />
              ) : null}
            </div>
          )}
            </>
          )}
        </>
      ) : null}

      {!buyerCleanLayout ? <LicenseRequestHistory /> : null}

      {canManage && !buyerCleanLayout && !hasActiveLicense ? (
        <LicenseCommercialActivationCollapsible
          systemInfo={systemInfo}
          onLicenseInstalled={onLicenseInstalled}
        />
      ) : null}
    </div>
  );
}
